/**
 * `pointerFocus` — which INPUT MODALITY put focus on a canvas surface.
 *
 * ## The bug, measured rather than reasoned about
 *
 * A reviewer clicks a quoted passage or a review note and a 2px blue ring
 * appears around it. Nobody asked for it, and it is not what `:focus-visible`
 * promises — the whole point of that selector is that a POINTER click does not
 * paint a ring.
 *
 * Measured in Chrome (narrate, mock corpus `/w/wettformat/article/1`), one real
 * `Input.dispatchMouseEvent` click on `.ela-quote__surface`:
 *
 * ```
 * activeElement          .ela-quote__surface
 * matches(':focus-visible')  true          ← the defect
 * outline                2px solid rgb(91, 143, 214)
 * ```
 *
 * ## Why `:focus-visible` is wrong here, and it is not the selector's fault
 *
 * The canvas surface is not focused BY the click. `createCanvasSurface` sets
 * `container.tabIndex = 0` and calls `container.focus()` from its own
 * `pointerdown` handler — it has to, or ⌘A and ⌘C would never reach the surface.
 * To the engine that is a SCRIPT focus, and Chromium's rule for `:focus-visible`
 * is "match unless this element was focused by the mouse". A script focus was not
 * focused by the mouse, so it matches — every time, on every click.
 *
 * That is a real upstream wrinkle (an FR against `@norkron/web-page-layout`: a
 * surface that takes focus on `pointerdown` should record the modality it took it
 * with). But the RING is elaborate's: it is elaborate's stylesheet that decides
 * what a focused surface looks like, so deciding WHEN to show it is elaborate's
 * job too, and this is the smallest honest way to know.
 *
 * ## ⛔ What this is not allowed to become
 *
 * A way to delete the focus indicator. **WCAG 2.4.7 (Focus Visible, AA)** is not
 * negotiable and a canvas surface is a real tab stop: `Tab` onto it MUST paint a
 * ring. So this marks the SOURCE and the stylesheet suppresses exactly one case —
 * `[data-focus-source="pointer"]` — leaving keyboard focus untouched.
 */

/** The attribute the stylesheet keys on. One name, stated once. */
export const FOCUS_SOURCE_ATTR = "data-focus-source";

/**
 * Record on `el` whether focus arrived by pointer or by keyboard, until it is
 * disposed. Returns the teardown.
 *
 * Listeners are CAPTURE-phase so the mark lands before the engine's own
 * `pointerdown` handler moves focus — a bubble-phase listener would run after
 * `focus()` and the first paint would already have happened.
 *
 * `keydown` promotes the mark to `keyboard` because a reader who clicks in and
 * then starts navigating by keyboard has become a keyboard user, and hiding the
 * ring from them is the same failure in the other direction. `blur` clears it, so
 * the next focus is classified on its own evidence rather than the last one's.
 */
export function trackFocusSource(el: HTMLElement): () => void {
  const mark = (source: "pointer" | "keyboard") => {
    el.setAttribute(FOCUS_SOURCE_ATTR, source);
  };
  const onPointerDown = () => mark("pointer");
  const onKeyDown = () => mark("keyboard");
  const onBlur = () => el.removeAttribute(FOCUS_SOURCE_ATTR);

  el.addEventListener("pointerdown", onPointerDown, true);
  el.addEventListener("keydown", onKeyDown, true);
  el.addEventListener("blur", onBlur, true);

  return () => {
    el.removeEventListener("pointerdown", onPointerDown, true);
    el.removeEventListener("keydown", onKeyDown, true);
    el.removeEventListener("blur", onBlur, true);
    el.removeAttribute(FOCUS_SOURCE_ATTR);
  };
}
